import ButtonGroupOverlay from 'components/ButtonGroupOverlay';
import Trash from 'components/Icons/Trash';
import ConfirmModal from 'components/modals/ConfirmModal';
import Immutable, { List } from 'immutable';
import React from 'react';
import { UpdateMultipleSubmittals } from 'records/interactions/UpdateMultipleSubmittals';
import { ModalState } from 'records/ModalState';
import { Submittal } from 'records/Submittal';
import * as toast from 'utils/toast';
import { useTranslator } from '@bluebeam/react-i18n';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { EditSubmittal } from 'records/interactions/EditSubmittal';

interface Props extends WithAppStoreProps {
  queriedSubmittals: List<Submittal>;
}

const SubmittalsSubheader = (props: Props) => {
  const { actions, appState } = props;
  const { translate } = useTranslator(['buttons', 'submittalsPage', 'filters']);
  const { selectedSubmittals } = appState;

  const handleEditMulti = (): void => {
    const updateMultipleSubmittals = new UpdateMultipleSubmittals();
    actions.createInteraction(updateMultipleSubmittals);
  };

  const hideEditButton = (): boolean => {
    const { submittals } = appState;
    const checkedSubmittals = submittals.filter((submittal: Submittal) => {
      return selectedSubmittals.includes(submittal.id);
    });
    return checkedSubmittals.some(
      (submittal) =>
        submittal.status.kind === 'SUB_APPROVED' ||
        submittal.status.kind === 'SUB_REJECTED' ||
        submittal.status.kind === 'SUB_RESUBMIT_REQUIRED'
    );
  };

  const showSingleEditModal = (): void => {
    const { submittals } = appState;
    const submittal: Submittal = submittals.get(
      selectedSubmittals.first()
    ) as Submittal;

    const editSubmittalInteraction = new EditSubmittal({
      submittal,
    });

    actions.createInteraction(editSubmittalInteraction);
  };

  const showSubbmittalsEditModal = (): void => {
    actions.clearInteractions();
    actions.clearAllModals();

    return selectedSubmittals.size === 1
      ? showSingleEditModal()
      : handleEditMulti();
  };

  const handleSelectAllClick = async (): Promise<void> => {
    if (props.queriedSubmittals.size !== selectedSubmittals.size) {
      const submittalsToSelect = props.queriedSubmittals.reduce(
        (result, submittal) => result.add(submittal.id),
        Immutable.Set<string>()
      );
      await actions.setSelectedSubmittals(submittalsToSelect);
    }
  };

  const handleDeselectMulti = (): void => {
    actions.setSelectedSubmittals(Immutable.Set<string>());
  };

  const handleDeleteSubmittals = async (): Promise<void> => {
    const submittalsToDelete = props.queriedSubmittals
      .filter((s) => selectedSubmittals.has(s.id))
      .reduce(
        (result, submittal) => result.set(submittal.id, submittal),
        Immutable.OrderedMap<string, Submittal>()
      );

    const modalState = new ModalState({
      id: 'ConfirmSubmittalsDelete',
      portal: 'main',
      open: true,
      component: ConfirmModal,
      props: {
        acceptButtonText: translate('buttons:delete'),
        acceptButtonType: 'delete',
        denyButtonText: translate('buttons:cancel'),
        headerText: translate(
          'submittalsPage:deleteSubmittalsBulkDialog.title'
        ),
        content: translate('submittalsPage:deleteSubmittalsBulkDialog.content'),
        acceptCallback: async (): Promise<void> => {
          try {
            await actions.clearAllModals();
            await actions.deleteSubmittalsBulk(submittalsToDelete);
            await actions.setSelectedSubmittals(Immutable.Set<string>());
            await actions.clearInteractions();
          } catch (err) {
            toast.error(
              translate(
                'submittalsPage:deleteSubmittalsBulkDialog.errorMessage'
              )
            );
            console.log(
              translate(
                'submittalsPage:deleteSubmittalsBulkDialog.errorMessage'
              ),
              err
            );
          }
        },
        denyCallback: (): void => {
          actions.clearAllModals();
        },
      },
    });

    await actions.createModal(modalState);
  };

  const isMultiEditing = (): boolean => {
    const { interactions } = appState;
    return (interactions.first<null>() as UpdateMultipleSubmittals) != null;
  };

  const display = (): boolean => {
    const editing = isMultiEditing();
    const itemSelected = selectedSubmittals.size > 0;
    return itemSelected && !editing;
  };

  return (
    <div className="column">
      {display() ? (
        <div>
          <div className="row">
            {!hideEditButton() ? (
              <ButtonGroupOverlay
                components={[
                  <div>
                    {`(${selectedSubmittals.size}) ${
                      selectedSubmittals.size > 1
                        ? translate('filters:items')
                        : translate('filters:item')
                    } ${translate('filters:selected')}`}
                  </div>,
                  <div
                    className="cursor-pointer"
                    onClick={handleSelectAllClick}
                  >
                    {translate('buttons:selectAll')}
                  </div>,
                  <div className="cursor-pointer" onClick={handleDeselectMulti}>
                    {translate('buttons:deselectAll')}
                  </div>,
                  <div
                    className="cursor-pointer"
                    onClick={showSubbmittalsEditModal}
                  >
                    {translate('buttons:edit')}
                  </div>,
                  <div
                    className="cursor-pointer"
                    onClick={handleDeleteSubmittals}
                  >
                    <div className="row justify-between items-center">
                      <Trash width="18" height="18" />
                      <span className="ml1">
                        {' '}
                        {translate('buttons:delete')}
                      </span>
                    </div>
                  </div>,
                ]}
              />
            ) : (
              <ButtonGroupOverlay
                components={[
                  <div>
                    {`(${selectedSubmittals.size}) ${
                      selectedSubmittals.size > 1
                        ? translate('filters:items')
                        : translate('filters:item')
                    } ${translate('filters:selected')}`}
                  </div>,
                  <div
                    className="cursor-pointer"
                    onClick={handleSelectAllClick}
                  >
                    {translate('buttons:selectAll')}
                  </div>,
                  <div className="cursor-pointer" onClick={handleDeselectMulti}>
                    {translate('buttons:deselectAll')}
                  </div>,
                  <div
                    className="cursor-pointer"
                    onClick={handleDeleteSubmittals}
                  >
                    <div className="row justify-between items-center">
                      <Trash width="18" height="18" />
                      <span className="ml1">
                        {' '}
                        {translate('buttons:delete')}
                      </span>
                    </div>
                  </div>,
                ]}
              />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default withAppStore<Props>(SubmittalsSubheader);
